/**
 * Press-and-hold controls. A click or a tap never completes a hold: the action runs only
 * after the pointer (or Space / Enter) stays down for the full duration. The kill switch
 * holds for 2 s, the T4 approval for 1.5 s. With reduced motion the sweep is replaced by
 * a numeric countdown.
 */

import {
  forwardRef,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useReducedMotion } from '../app/media.js';
import { formatSecondsLeft } from '../lib/format.js';
import { Icon } from './icons.js';

export const KILL_SWITCH_HOLD_MS = 2_000;
export const T4_HOLD_MS = 1_500;
/** Loosening a policy limit: fixed, so the setting for T4 holds can never shorten it. */
export const POLICY_HOLD_MS = 1_500;
/** Tüm yetkileri iptal et. */
export const REVOKE_HOLD_MS = 2_000;

/** "1,5 saniye", "2 saniye" for spoken labels. */
export function formatHoldSeconds(ms: number): string {
  const seconds = ms / 1000;
  return `${Number.isInteger(seconds) ? seconds : seconds.toFixed(1).replace('.', ',')} saniye`;
}

export interface HoldHandle {
  start(): void;
  cancel(): void;
}

interface HoldOptions {
  durationMs: number;
  disabled: boolean;
  onComplete: () => void;
  onEarlyRelease?: () => void;
  /** The hold began (pointer down, Space, Enter or a keyboard shortcut): T4 asks for its token here. */
  onStart?: () => void;
}

export function useHold({ durationMs, disabled, onComplete, onEarlyRelease, onStart }: HoldOptions) {
  const [holding, setHolding] = useState(false);
  const [remaining, setRemaining] = useState(durationMs);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAt = useRef(0);
  const active = useRef(false);
  const unlisten = useRef<(() => void) | null>(null);
  const callbacks = useRef({ onComplete, onEarlyRelease, onStart });
  callbacks.current = { onComplete, onEarlyRelease, onStart };

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    if (ticker.current) clearInterval(ticker.current);
    timer.current = null;
    ticker.current = null;
    unlisten.current?.();
    unlisten.current = null;
    active.current = false;
    setHolding(false);
    setRemaining(durationMs);
  }, [durationMs]);

  const start = useCallback(() => {
    if (disabled || active.current) return;
    active.current = true;
    startedAt.current = Date.now();
    callbacks.current.onStart?.();
    setHolding(true);
    setRemaining(durationMs);
    ticker.current = setInterval(() => {
      setRemaining(Math.max(0, durationMs - (Date.now() - startedAt.current)));
    }, 100);
    timer.current = setTimeout(() => {
      clear();
      callbacks.current.onComplete();
    }, durationMs);
    // A hold that loses the window (app switch, screen lock) would never see its key-up or
    // pointer-up, so it must not run on to completion: it is released instead.
    const release = () => {
      if (!active.current) return;
      clear();
      callbacks.current.onEarlyRelease?.();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') release();
    };
    window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', onVisibility);
    unlisten.current = () => {
      window.removeEventListener('blur', release);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [clear, disabled, durationMs]);

  const cancel = useCallback(() => {
    if (!active.current) return;
    clear();
    callbacks.current.onEarlyRelease?.();
  }, [clear]);

  useEffect(() => clear, [clear]);

  useEffect(() => {
    if (disabled && active.current) clear();
  }, [disabled, clear]);

  const handlers = {
    onPointerDown: (event: PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return;
      try {
        // Keep receiving pointerup even if the finger slides off the button.
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Not supported or no active pointer (synthetic events): the hold still works.
      }
      start();
    },
    onPointerUp: () => cancel(),
    onPointerCancel: () => cancel(),
    onLostPointerCapture: () => cancel(),
    // Focus leaving the button mid-hold (Tab, a dialog) ends a keyboard hold too.
    onBlur: () => cancel(),
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      if (!event.repeat) start();
    },
    onKeyUp: (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      cancel();
    },
    onContextMenu: (event: { preventDefault(): void }) => event.preventDefault(),
    // A plain click (or a keyboard activation) must never complete a hold.
    onClick: (event: { preventDefault(): void }) => event.preventDefault(),
  };

  return { holding, remaining, progress: 1 - remaining / durationMs, handlers, start, cancel };
}

/** Kill switch (Durdur): the only striped control. Hold 2 s for a hard stop. */
export function KillSwitch({
  variant,
  stopped,
  onStop,
  onEarlyRelease,
}: {
  variant: 'compact' | 'desktop';
  stopped: boolean;
  onStop: () => void;
  onEarlyRelease: () => void;
}) {
  const reducedMotion = useReducedMotion();
  const { holding, remaining, handlers } = useHold({
    durationMs: KILL_SWITCH_HOLD_MS,
    disabled: stopped,
    onComplete: onStop,
    onEarlyRelease,
  });
  const label = stopped ? 'Durduruldu' : variant === 'desktop' ? 'Acil durdur' : 'Durdur';
  const aria = stopped
    ? 'Durduruldu. Devam etmek için banttaki Devam et'
    : holding
      ? `Durdur: basılı tutuluyor, ${formatSecondsLeft(remaining)} kaldı`
      : variant === 'desktop'
        ? 'Acil durdur: ekibi durdurmak için 2 saniye basılı tut'
        : 'Durdur: ekibi acil durdurmak için 2 saniye basılı tut';
  const content = (
    <>
      <span className="kill__square" aria-hidden="true" />
      {holding ? (
        <span className="kill__text">
          <span className="kill__label kill__label--small">Basılı tut…</span>
          <span className="kill__sub mono">{formatSecondsLeft(remaining)}</span>
        </span>
      ) : (
        <span className="kill__text">
          <span className="kill__label">{label}</span>
          {variant === 'desktop' && !stopped ? <span className="kill__sub key">2 sn basılı tut</span> : null}
        </span>
      )}
    </>
  );
  return (
    <button
      type="button"
      className={`kill kill--${variant}${stopped ? ' kill--stopped' : ''}${holding ? ' kill--holding' : ''}`}
      aria-label={aria}
      aria-disabled={stopped || undefined}
      style={{ ['--hold-ms' as string]: `${KILL_SWITCH_HOLD_MS}ms` }}
      {...handlers}
    >
      <span className="kill__band" aria-hidden="true" />
      <span className="kill__content">{content}</span>
      {holding && !reducedMotion ? (
        <span className="hold-fill hold-fill--danger" aria-hidden="true">
          <span className="kill__content kill__content--on-fill">{content}</span>
        </span>
      ) : null}
    </button>
  );
}

/**
 * T4 approval: 1.5 s hold on the detail screen only, never from a list. Disabled with a
 * plain reason when the data is stale or the executor is stopped.
 */
export const T4HoldButton = forwardRef<
  HoldHandle,
  {
    label: string;
    sublabel: string;
    disabledReason: string | null;
    onComplete: () => void;
    onEarlyRelease: () => void;
    compact?: boolean;
    extra?: ReactNode;
    /** Hold length; the T4 approval reads it from policy (approval_t4_hold). */
    durationMs?: number;
    /** What the hold does, for the spoken label: "onaylamak", "gevşetmek". */
    holdVerb?: string;
    /** The hold began; the T4 approval asks the server for its hold token here (2.6). */
    onStart?: () => void;
  }
>(function T4HoldButton(
  {
    label,
    sublabel,
    disabledReason,
    onComplete,
    onEarlyRelease,
    compact = false,
    extra,
    durationMs = T4_HOLD_MS,
    holdVerb = 'onaylamak',
    onStart,
  },
  ref,
) {
  const reducedMotion = useReducedMotion();
  const disabled = disabledReason !== null;
  const { holding, remaining, handlers, start, cancel } = useHold({
    durationMs,
    disabled,
    onComplete,
    onEarlyRelease,
    ...(onStart ? { onStart } : {}),
  });
  useImperativeHandle(ref, () => ({ start, cancel }), [start, cancel]);
  const content = (
    <>
      <Icon name="lock" size={18} />
      <span className="t4hold__text">
        <span className="t4hold__label">{disabled ? disabledReason : label}</span>
        <span className="t4hold__sub">
          {holding && reducedMotion ? `${formatSecondsLeft(remaining)} kaldı` : sublabel}
        </span>
      </span>
      {extra}
    </>
  );
  return (
    <button
      type="button"
      className={`t4hold${compact ? ' t4hold--compact' : ''}${holding ? ' t4hold--holding' : ''}`}
      disabled={disabled}
      aria-label={
        disabled
          ? `${label}: ${disabledReason}`
          : holding
            ? `${label}: basılı tutuluyor, ${formatSecondsLeft(remaining)} kaldı`
            : `${label}: ${holdVerb} için ${formatHoldSeconds(durationMs)} basılı tut. ${sublabel}`
      }
      style={{ ['--hold-ms' as string]: `${durationMs}ms` }}
      {...handlers}
    >
      <span className="t4hold__content">{content}</span>
      {holding && !reducedMotion ? (
        <span className="hold-fill hold-fill--accent" aria-hidden="true">
          <span className="t4hold__content t4hold__content--on-fill">{content}</span>
        </span>
      ) : null}
    </button>
  );
});
